import React, { useEffect, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import CertificateWizard from './components/CertificateWizard';
import LocationPage from './pages/LocationPage';
import BlogArticlePage from './pages/BlogArticlePage';
import BlogListPage from './pages/BlogListPage';
import CookieBanner from './components/CookieBanner';
import GoogleTracking from './components/GoogleTracking';
import SEOMetaTags from './components/SEOMetaTags';
import TawkWidget from './components/TawkWidget';
// SnapCertPopup scos 08.10.2026 (interstitial intruziv pe mobil - risc SEO + pierdere conversii). Componenta ramane in components/.
import { API_URL } from './config';
import { captureAttribution } from './utils/attribution';

// Analitice: retine sursa vizitei (prima pagina) - sessionStorage, fara cookie
captureAttribution();

// Lazy loaded - nu sunt pe critical path
const AdminLogin = React.lazy(() => import('./components/AdminLogin'));
const AdminDashboard = React.lazy(() => import('./components/AdminDashboard'));
const ProtectedRoute = React.lazy(() => import('./components/ProtectedRoute'));
const TermeniConditii = React.lazy(() => import('./pages/TermeniConditii'));
const PoliticaConfidentialitate = React.lazy(() => import('./pages/PoliticaConfidentialitate'));
const PaymentSuccess = React.lazy(() => import('./pages/PaymentSuccess'));
const PaymentCancel = React.lazy(() => import('./pages/PaymentCancel'));
const NotFound = React.lazy(() => import('./pages/NotFound'));
const ReviewThankYou = React.lazy(() => import('./pages/ReviewThankYou'));

// Componenta helper pentru redirect cu scroll la secțiune
const ScrollToSection = ({ sectionId }) => {
  useEffect(() => {
    // După redirect, scroll la secțiune
    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  }, [sectionId]);

  return <Navigate to="/" replace state={{ scrollTo: sectionId }} />;
};

function App() {
  // Dynamic Favicon Update
  useEffect(() => {
    const updateFavicon = async () => {
      try {
        const response = await fetch(`${API_URL}/api/orders/company`);
        const data = await response.json();

        if (data.favicon) {
          // Update favicon link tag
          let link = document.querySelector("link[rel*='icon']");

          if (!link) {
            link = document.createElement('link');
            link.rel = 'icon';
            document.head.appendChild(link);
          }

          link.href = `${API_URL}${data.favicon}`;
          console.log('✅ Favicon updated:', link.href);
        }
      } catch (error) {
        console.error('Error updating favicon:', error);
        // Fallback - păstrează favicon-ul default din public/
      }
    };

    updateFavicon();
  }, []);
  return (
    <BrowserRouter>
      {/* SEO Meta Tags - injectare DOAR pe pagini publice (nu /admin/*) */}
      <SEOMetaTags />

      {/* Cookie Consent Banner (GDPR) - afișat pe toate paginile publice */}
      <CookieBanner />

      {/* Google Analytics + GTM - injectare doar cu consimțământ "Statistici" */}
      <GoogleTracking />

      {/* Tawk.to Live Chat - injectare dacă enabled în Admin Settings */}
      <TawkWidget />

      <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div></div>}>
        <Routes>
          <Route path="/" element={<CertificateWizard />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/termeni-conditii" element={<TermeniConditii />} />
          <Route path="/politica-confidentialitate" element={<PoliticaConfidentialitate />} />

          {/* Rute SPA - redirect la homepage cu scroll la secțiune */}
          <Route path="/preturi" element={<ScrollToSection sectionId="preturi" />} />
          <Route path="/majorare-capital-social" element={<ScrollToSection sectionId="preturi" />} />
          <Route path="/despre-noi" element={<ScrollToSection sectionId="despre" />} />

          {/* SEO Location Pages - certificat constatator per oraș */}
          <Route path="/certificat-constatator/:slug" element={<LocationPage />} />

          {/* Blog - articole SEO */}
          <Route path="/blog" element={<BlogListPage />} />
          <Route path="/blog/:slug" element={<BlogArticlePage />} />

          {/* Review pages - pentru email rating */}
          <Route path="/review/multumim" element={<ReviewThankYou />} />
          <Route path="/review/error" element={<ReviewThankYou />} />

          {/* 404 - Catch all unmatched routes */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
